import routes from "@/config/routes.ts";
import client from "@/data/client";
import API_ENDPOINTS from "@/data/client/endpoints.ts";
import { DefaultErrorResponse, UserExercises } from "@/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { enqueueSnackbar } from "notistack";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";

export function useUserExercises() {
  const {
    data: regularExercisesData,
    isLoading: isLoadingRegularExercises,
    error: errorRegularExercises,
  } = useQuery<UserExercises, Error>(
    [API_ENDPOINTS.USER_EXERCISES],
    () => client.users.exercises(),
    {
      select: (data) => {
        const item = {
          ...data.item,
          done:
            data.item?.done.map((exercise) => ({
              ...exercise,
              isPot: false,
            })) || [],
          toDo:
            data.item?.toDo.map((exercise) => ({
              ...exercise,
              isPot: false,
            })) || [],
          count_corrections: data.item?.count_corrections || [],
          count_booked_corrections: data.item?.count_booked_corrections || [],
          exercises_done_into_current_range_product:
            data.item?.exercises_done_into_current_range_product || 0,
          userAnswers: data.item?.userAnswers || {},
        };

        return {
          ...data,
          item,
        };
      },
      refetchOnWindowFocus: !location.href.includes("pot"),
    },
  );

  const {
    data: potExercisesData,
    isLoading: isLoadingPotExercises,
    error: errorPotExercises,
  } = useQuery<UserExercises, Error>(
    [API_ENDPOINTS.USER_EXERCISES_POT],
    () => client.users.exercises_pot(),
    {
      select: (data) => {
        const item = {
          ...data.item,
          done:
            data.item?.done.map((exercise) => ({ ...exercise, isPot: true })) ||
            [],
          toDo:
            data.item?.toDo.map((exercise) => ({ ...exercise, isPot: true })) ||
            [],
          count_corrections: data.item?.count_corrections || [],
          count_booked_corrections: data.item?.count_booked_corrections || [],
          exercises_done_into_current_range_product:
            data.item?.exercises_done_into_current_range_product || 0,
          userAnswers: data.item?.userAnswers || {},
        };
        return {
          ...data,
          item,
        };
      },
      refetchOnWindowFocus: !location.href.includes("pot"),
    },
  );

  const isLoading = isLoadingRegularExercises || isLoadingPotExercises;
  const error = errorRegularExercises || errorPotExercises;

  const allExercises = useMemo(
    () => ({
      done: [
        ...(regularExercisesData?.item?.done || []),
        ...(potExercisesData?.item?.done || []),
      ],
      toDo: [
        ...(regularExercisesData?.item?.toDo || []),
        ...(potExercisesData?.item?.toDo || []),
      ],
      count_corrections: regularExercisesData?.item?.count_corrections || [],
      count_booked_corrections:
        regularExercisesData?.item?.count_booked_corrections || [],
      exercises_done_into_current_range_product:
        regularExercisesData?.item?.exercises_done_into_current_range_product ||
        0,
      userAnswers: potExercisesData?.item?.userAnswers || {},
    }),
    [regularExercisesData, potExercisesData],
  );

  return {
    exercises: allExercises,
    isLoading,
    error,
  };
}

export function useCorrectExerciseMutation() {
  return useMutation(client.exercise.correctExercise, {
    onError: (error) => {
      const err = error as AxiosError<DefaultErrorResponse>;
      enqueueSnackbar(err.response?.data.message, {
        variant: "error",
      });
    },
  });
}

export function useBookMockTestMutation(callback?: () => void) {
  const navigate = useNavigate();

  return useMutation(client.exercise.bookMockTest, {
    onSuccess: (data) => {
      if (data.status === 200) {
        enqueueSnackbar(data?.message, {
          variant: "success",
        });
        if (callback) {
          callback();
        }
        navigate(routes.exercises, {
          replace: true,
        });
      } else {
        enqueueSnackbar("Something went wrong. please retry", {
          variant: "error",
        });
      }
    },
    onError: (error) => {
      const err = error as AxiosError<DefaultErrorResponse>;
      enqueueSnackbar(err.response?.data.message, {
        variant: "error",
      });
    },
  });
}

export function useCheckBookLiveCorrectionMutation() {
  const navigate = useNavigate();

  return useMutation(client.exercise.checkBookLiveCorrection, {
    onSuccess: (data) => {
      if (typeof data == "string") {
        enqueueSnackbar(data, {
          variant: "error",
        });

        return;
      }

      navigate("correction/book-live");
    },
    onError: (error) => {
      const err = error as AxiosError<DefaultErrorResponse>;

      enqueueSnackbar(err.response?.data.message, {
        variant: "error",
      });
    },
  });
}

export function useBookLiveCorrectionMutation(callback?: () => void) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation(client.exercise.bookLiveCorrection, {
    onSuccess: (data) => {
      if (data.status === 200) {
        enqueueSnackbar(data.message, {
          variant: "success",
        });
        if (callback) {
          callback();
        }
        navigate(routes.exercises, {
          replace: true,
        });
      } else {
        enqueueSnackbar("Something went wrong. please retry", {
          variant: "error",
        });
      }
    },
    onError: (error) => {
      const err = error as AxiosError<DefaultErrorResponse>;
      enqueueSnackbar(err.response?.data.message, {
        variant: "error",
      });
    },
    onSettled: () => {
      void queryClient.invalidateQueries([API_ENDPOINTS.USER_EXERCISES]);
    },
  });
}
